// Kiest per boring een vaste verticale schaal (1:N) voor het boorprofiel op papier.
//
// Bewust per boring, niet één schaal voor de hele export: zet je een boring van 0,5 m en een van
// 5 m op dezelfde schaal (zoals TerraIndex doet), dan wordt de korte een onleesbaar streepje.
// Elke boring krijgt de grootste standaardschaal waarbij de hele tekening -- kop, profiel,
// beschrijvingen en legenda -- nog op de pagina past. Geen willekeurige tussenschalen, zodat
// je er met een liniaal op een print aan kunt meten.
import { buildBoorprofielSvg } from './boorprofiel.js';

/** Standaardschalen, van groot naar klein. 1:500 is het vangnet voor heel diepe boringen. */
export const VERTICAL_SCALES = [10, 20, 25, 50, 100, 200, 500];

/** Afstand tussen de dieptestreepjes per schaal (cm): op papier steeds minstens ~4 mm. */
const TICK_STEP_CM = { 10: 10, 20: 10, 25: 25, 50: 50, 100: 50, 200: 100, 500: 200 };

/** Breedte en hoogte (px) uit de viewBox van een boorprofiel-SVG. */
export function svgSize(svg) {
  const vb = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(svg) || [];
  return { w: parseFloat(vb[1]) || 800, h: parseFloat(vb[2]) || 1000 };
}

/**
 * Het boorprofiel op een vaste schaal, passend in een vak van widthMm × maxHeightMm.
 *
 * De breedte van de tekening ligt vast (profiel + beschrijving + legenda) en vult de
 * inhoudsbreedte van de pagina; daarmee ligt het aantal mm per px vast, en is 1:N gewoon
 * pxPerCm = (10 / N) / mmPerPx. Past zelfs 1:500 niet (een boring met heel veel lagen, waarvan
 * de tekst niet meeschaalt), dan wordt de tekening verkleind tot hij past en heet de schaal
 * 'ongeveer' -- nooit afkappen.
 *
 * @param {object} d  het `d`-object voor buildBoorprofielSvg
 * @param {{widthMm:number, maxHeightMm:number}} box
 * @returns {{svg:string, w:number, h:number, drawW:number, drawH:number, scale:number, approximate:boolean}}
 */
export function scaleBoorprofiel(d, { widthMm, maxHeightMm }) {
  let result = null;
  for (const scale of VERTICAL_SCALES) {
    result = renderAtScale(d, widthMm, scale);
    if (result.drawH <= maxHeightMm) return result;
  }
  return shrinkToFit(result, maxHeightMm);
}

/** Het boorprofiel op schaal 1:scale, met de breedte van de tekening op widthMm. */
export function renderAtScale(d, widthMm, scale) {
  const mmPerPx = widthMm / svgSize(buildBoorprofielSvg(d)).w;
  const svg = buildBoorprofielSvg({ ...d, pxPerCm: (10 / scale) / mmPerPx, tickStepCm: TICK_STEP_CM[scale] });
  const size = svgSize(svg);
  return { svg, ...size, drawW: widthMm, drawH: size.h * mmPerPx, scale, approximate: false };
}

/** Vangnet: verklein de tekening tot hij in maxHeightMm past; de schaal heet dan 'ongeveer'. */
export function shrinkToFit(result, maxHeightMm) {
  const shrink = maxHeightMm / result.drawH;
  return {
    ...result,
    drawW: result.drawW * shrink,
    drawH: maxHeightMm,
    scale: Math.round(result.scale / shrink),
    approximate: true,
  };
}

/** "Schaal verticaal 1:50" (of "ca. 1:612" als de tekening verkleind moest worden). */
export const scaleLabel = ({ scale, approximate }) => `Schaal verticaal ${approximate ? 'ca. ' : ''}1:${scale}`;
